// AgentCategoryChips — primary category navigation for the /agents catalog.
// Horizontal pills (All, My Agents, then categories) so browsing doesn't
// hide behind a dropdown.

import React, { useMemo } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

function AgentCategoryChips({
  items,
  activeId,
  onChange,
  padX = 20,
}) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(padX), [padX]);

  if (!items?.length) return null;

  return (
    <View style={styles.wrap}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.row}
      >
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <Pressable
              key={item.id}
              onPress={() => onChange?.(item.id)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={item.label}
              style={({ hovered, pressed }) => [
                styles.chip,
                {
                  backgroundColor: active
                    ? colors.solid_color_tertiary
                    : (hovered || pressed ? colors.solid_color_tertiary : colors.solid_color_block),
                  borderColor: active
                    ? colors.border_color_primary
                    : colors.border_color_secondary,
                },
              ]}
            >
              {item.icon ? (
                <Icon
                  name={item.icon}
                  size={11}
                  solid={!!item.iconSolid && active}
                  color={active ? colors.text_color_primary : colors.text_color_secondary}
                />
              ) : null}
              <Text
                size="xs"
                weight={active ? 'bold' : 'semibold'}
                color={active ? 'primary' : 'secondary'}
                numberOfLines={1}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

AgentCategoryChips.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    iconSolid: PropTypes.bool,
  })),
  activeId: PropTypes.string,
  onChange: PropTypes.func,
  padX: PropTypes.number,
};

const createStyles = (padX) => StyleSheet.create({
  wrap: {
    width: '100%',
    marginLeft: -padX,
    marginRight: -padX,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingLeft: padX,
    paddingRight: padX,
    paddingVertical: 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});

export default React.memo(AgentCategoryChips);
